import { useState, useEffect } from "react";
import {
  generatePath,
  useLocation,
  useNavigate,
  useParams
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { AxiosError } from "axios";
import type { IButton } from "libs/design-system/src/types/Button/button";
import {
  Button,
  Checkbox,
  Input,
  Notification,
  SkeletonBase,
  SkeletonLoader,
  arrayToCommaSeparated,
  commaSeparatedToArray
} from "@unified-checkout/design-system";
import TextArea from "libs/design-system/src/components/TextArea";
import {
  SYSTEM_EVENTS_SRC_FEATURES
} from "@app/dashboard/constants/tracking_events/system";
import type { IField } from "@app/dashboard/types/outerLayout/common";
import type { IRoleManagementLocationProps } from "@app/dashboard/types/innerLayout/user_management";
import { INNER_ROUTES } from "@app/dashboard/constants/inner-routes";
import { onToasterShowHandler } from "@app/dashboard/utils/tracking/onToasterShowHandler";
import { isFormValid, required, validateField } from "@app/dashboard/utils/formValidations";
import { useRoleManageMentAPICalls } from "./utils";
import { handleToasterClose, NotificationSharedProps } from "../../components/modals/utils";
import { arraysValuesCompare } from "@app/dashboard/utils/arraysValuesCompare";
import { ComponentError } from "@app/dashboard/components/shared/ComponentError";
import {
  CollapsibleSection
} from "@app/dashboard/components/inner_layout/CollapsibleSection";
import { ConfirmationModal as CancelRoleCreationModal } from "../../components/modals/ConfirmationModal";
import { DeleteModal as DeleteRoleModal } from "../../components/modals/DeleteModal";
import styles from "./role-management.module.scss";

const COLLAPSIBLE_SECTION_WIDTH = "100%";
const COLLAPSIBLE_SECTION_HEIGHT = "4.25rem";

const LABEL_WIDTH = "32rem";
const LABEL_HEIGHT = "1rem";

const INPUT_WIDTH = "32rem";
const INPUT_HEIGHT = "2.5rem";

const TEXTAREA_WIDTH = "32rem";
const TEXTAREA_HEIGHT = "8rem";

export const RoleManagement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname, state } = useLocation() as { pathname: string; state: IRoleManagementLocationProps };
  const { id: roleId = '' } = useParams<{ id: string }>();

  const derivedPageType = pathname.includes('create-new-role') ? 'create_new_role' : 'role_details';

  const SESSION_KEY = `role-management-state-${roleId || 'new'}`;

  useEffect(() => {
    if (state) {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify({ userId: state.userId, preventEdit: state.preventEdit }));
    }
  }, [state]);

  const persistedState = JSON.parse(sessionStorage.getItem(SESSION_KEY) || 'null');
  const pageType = derivedPageType;
  const userId = state?.userId ?? persistedState?.userId;
  const preventEdit = state?.preventEdit ?? persistedState?.preventEdit ?? false;

  const [isDeleteRoleModalOpen, setIsDeleteRoleModalOpen] = useState(false);

  const FIELDS_NAMES = {
    TITLE: `${pageType}-title-input`,
    DESCRIPTION: `${pageType}-desc-input`,
    PERMISSIONS: `${pageType}-perms`
  };

  const fieldsInitialState = {
    [FIELDS_NAMES.TITLE]: {
      value: '',
      isValid: undefined,
      errorMsg: '',
      validations: [required]
    },
    [FIELDS_NAMES.DESCRIPTION]: {
      value: '',
      isValid: undefined,
      errorMsg: '',
      validations: [required]
    },
    [FIELDS_NAMES.PERMISSIONS]: {
      value: '',
      isValid: undefined,
      errorMsg: '',
      validations: [required]
    }
  };

  const [fields, setFields] = useState<IField>(fieldsInitialState);
  const [initialFields, setInitialFields] = useState<IField>(fieldsInitialState);
  const [isCancelRoleCreationModalOpen, setIsCancelRoleCreationModalOpen] = useState(false);

  const {
    toasterInfo,
    setToasterInfo,

    isPermsCatalogLoading,
    isPermsCatalogError,
    permsCatalogError,
    permsCatalog,

    isCreateOrUpdateRolePending,
    isCreateOrUpdateRoleError,
    createOrUpdateRoleError,
    mutateCreateOrUpdateRole,

    isRetrievedRoleLoading,
    isRetrievedRoleError,
    retrievedRoleError,
    retrievedRole,

  } = useRoleManageMentAPICalls({
    pageType,
    fields,
    fieldsNames: FIELDS_NAMES,
    userId,
    roleId,
    preventEdit
  });

  const isRoleDeletable =
    pageType === 'role_details' &&
    preventEdit === false &&
    retrievedRole?.is_custom === true &&
    retrievedRole?.user_count === 0;


  useEffect(() => {
    if (pageType === 'role_details' && retrievedRole) {

      const selectedPerms: string[] = [...new Set<string>(
        retrievedRole.permissions?.flatMap((role: any) =>
          role.sub_list
            .filter((p: any) => p.selected)
            .flatMap((p: any) => [
              p.perm_name as string,
              ...(p.dependent || []),
              ...(p.hidden_perms || [])
            ])
        )
      )];

      const commaSeparatedSelectedPerms = arrayToCommaSeparated(selectedPerms);

      const initialFieldsState = {
        [FIELDS_NAMES.TITLE]: {
          value: retrievedRole.title,
          isValid: true,
          errorMsg: '',
          validations: [required]
        },
        [FIELDS_NAMES.DESCRIPTION]: {
          value: retrievedRole.description || '',
          isValid: true,
          errorMsg: '',
          validations: [required]
        },
        [FIELDS_NAMES.PERMISSIONS]: {
          value: commaSeparatedSelectedPerms || '',
          isValid: true,
          errorMsg: '',
          validations: [required]
        }
      };

      setFields(initialFieldsState);
      setInitialFields(initialFieldsState);
    }
  }, [retrievedRole]);

  const onChangeHandler = (fieldName: string, value: string) => {

    setFields(prevState => ({
      ...prevState,
      [fieldName]: {
        ...prevState[fieldName],
        value: value
      }
    }));

    validateField(fields, setFields, fieldName, value);
  };

  type TFieldName = typeof FIELDS_NAMES[keyof typeof FIELDS_NAMES];
  const onPermsChangeHandler = (
    fieldName: TFieldName,
    permsGroup: Record<string, any>[],
    value: string,
    dependents: string[],
    hiddenPerms: string[],
    checked: boolean,
    action: 'click' | 'change'
  ) => {

    const permsArr = commaSeparatedToArray(fields[fieldName].value || '');

    if (checked) {

      if (!fields[fieldName].value?.includes(value)) {
        permsArr.push(value);

        if (action === 'click') {
          [...dependents, ...(hiddenPerms || [])].forEach(dependentPerm => {
            if (!permsArr.includes(dependentPerm)) {
              permsArr.push(dependentPerm);
            };
          });
        };
        
        const selectedPermsWithHiddenPerms = new Set(permsArr);
        // Create a map of perm_name to permission object for quick lookup
        const permsByName = new Map(permsGroup.map(p => [p.perm_name, p]));

        // Iterate over the selected permissions and add their hidden permissions to the set
        // This ensures that all hidden permissions are included, even if they are not directly selected
        selectedPermsWithHiddenPerms.forEach(permName => {
          
          const perm = permsByName.get(permName);
          // If the permission exists and has hidden permissions, add them to the set
          perm?.hidden_perms?.forEach((hiddenPerm: string) => {
            selectedPermsWithHiddenPerms.add(hiddenPerm);
          });
        });

        const updatedCommaSeparatedPerms = arrayToCommaSeparated([...selectedPermsWithHiddenPerms]);

        setFields(prevState => ({
          ...prevState,
          [fieldName]: {
            ...prevState[fieldName],
            value: updatedCommaSeparatedPerms
          }
        }));

        validateField(fields, setFields, fieldName, updatedCommaSeparatedPerms);
      }
    } else {
      const updatedPermsArr = permsArr.filter(perm => perm !== value);

      if (action === 'click') {
        // Collect all perms being removed (the unchecked perm + its dependents)
        const permsToRemove = new Set<string>([value]);

        permsGroup.forEach(perm => {
          if (perm.dependent?.includes(value) && updatedPermsArr.includes(perm.perm_name)) {
            permsToRemove.add(perm.perm_name);
          }
        });

        // Remove dependent perms
        permsToRemove.forEach(p => {
          const idx = updatedPermsArr.indexOf(p);
          if (idx !== -1) updatedPermsArr.splice(idx, 1);
        });

        // Remove hidden perms only if no remaining selected perm still needs them
        const allHiddenPermsToCheck = new Set<string>(hiddenPerms);
        permsGroup.forEach(perm => {
          if (permsToRemove.has(perm.perm_name)) {
            perm.hidden_perms?.forEach((hp: string) => allHiddenPermsToCheck.add(hp));
          }
        });

        allHiddenPermsToCheck.forEach(hp => {
          const isIncludedInAnotherSelectedPerm = permsGroup.some(otherPerm =>
            updatedPermsArr.includes(otherPerm.perm_name) &&
            otherPerm.hidden_perms?.includes(hp)
          );
          if (!isIncludedInAnotherSelectedPerm) {
            const idx = updatedPermsArr.indexOf(hp);
            if (idx !== -1) updatedPermsArr.splice(idx, 1);
          }
        });
      };

      const updatedCommaSeparatedPerms = arrayToCommaSeparated(updatedPermsArr);

      setFields(prevState => ({
        ...prevState,
        [fieldName]: {
          ...prevState[fieldName],
          value: updatedCommaSeparatedPerms
        }
      }));

      validateField(fields, setFields, fieldName, updatedCommaSeparatedPerms);
    }
  };

  const isDirty = pageType === 'role_details' ?
    Object.keys(fields).some(key =>
      !arraysValuesCompare(
        commaSeparatedToArray(fields[key].value || ''),
        commaSeparatedToArray(initialFields[key].value || '')
      )
    )
    :
    true;

  const isDisabled = !isFormValid(fields) || isCreateOrUpdateRolePending || toasterInfo?.open || !isDirty;

  const isPrimaryActionBtnDisabled =
    pageType === 'create_new_role' ?
      isDisabled || isPermsCatalogLoading
      :
      isDisabled || isRetrievedRoleLoading;

  const isPrimaryActionBtnLoading = isCreateOrUpdateRolePending;

  const actionBtnsProps: IButton[] = [
    {
      color: 'secondary',
      variant: 'link',
      text: t('form.cancel'),
      onClickHandler: () => {
        if (pageType === 'create_new_role') {
          setIsCancelRoleCreationModalOpen(true);
        } else {
          navigate(INNER_ROUTES.MANAGE_ROLES);
        }
      }
    },
    {
      color: 'primary',
      variant: 'contained',
      text: pageType === 'create_new_role' ? t('innerLayout.userManagementPage.form.createRole') : t('form.save'),
      disabled: isPrimaryActionBtnDisabled,
      loading: isPrimaryActionBtnLoading,
      onClickHandler: isFormValid(fields) ? mutateCreateOrUpdateRole : undefined
    }
  ];

  const isPermsSectionLoading = pageType === 'create_new_role' ?
    isPermsCatalogLoading
    :
    isRetrievedRoleLoading;

  const isPermsSectionError = pageType === 'create_new_role' ?
    isPermsCatalogError
    :
    isRetrievedRoleError;

  const permsSectionError = (
    ((
      pageType === 'create_new_role' ? permsCatalogError
        :
        retrievedRoleError
    ) as AxiosError)?.response?.data as any)?.message
    || t('errors.somethingWentWrong');

  const permsSectionListing = pageType === 'create_new_role' ?
    permsCatalog
    :
    retrievedRole?.permissions || [];


  const isInputsLoading =
    pageType === 'create_new_role' ?
      false
      :
      isRetrievedRoleLoading;

  const isInputsDisabled =
    pageType === 'role_details' &&
    (
      retrievedRole?.is_custom === false ||
      preventEdit
    );


  const isChecked = (perm: Record<string, any>) => {
    const currentSelectedPerms = commaSeparatedToArray(fields[FIELDS_NAMES.PERMISSIONS].value || '');
    return currentSelectedPerms.includes(perm.perm_name);
  };

  return (
    <>
      <div className={styles["role-management"]}>

        {
          isRoleDeletable &&
          <div className={styles["role-management__delete-role-btn"]}>
            <Button
              size="md"
              color='danger'
              variant='outlined'
              text={t('innerLayout.userManagementPage.form.deleteRole')}
              onClickHandler={() => setIsDeleteRoleModalOpen(true)}
            />
          </div>
        }

        <div className={styles["role-management__section"]}>

          <div className={styles["role-management__section__text-box"]}>

            <div className={styles["role-management__section__text-box__title"]}>
              {t('innerLayout.userManagementPage.roleManagementPage.info')}
            </div>
            <div className={styles["role-management__section__text-box__desc"]}>
              {t('innerLayout.userManagementPage.roleManagementPage.infoHint')}
            </div>
          </div>

          <div className={styles["role-management__section__actions-box"]}>
            {
              isInputsLoading ?
                <>
                  <div className={styles["role-management__section__actions-box__input-wrapper"]}>
                    <SkeletonBase
                      key={`role-management-${FIELDS_NAMES.TITLE}-label-loader`}
                      width={LABEL_WIDTH}
                      height={LABEL_HEIGHT}
                    />
                    <SkeletonBase
                      key={`role-management-${FIELDS_NAMES.TITLE}-field-loader`}
                      width={INPUT_WIDTH}
                      height={INPUT_HEIGHT}
                    />
                  </div>
                  <div className={styles["role-management__section__actions-box__input-wrapper"]}>
                    <SkeletonBase
                      key={`role-management-${FIELDS_NAMES.DESCRIPTION}-label-loader`}
                      width={LABEL_WIDTH}
                      height={LABEL_HEIGHT}
                    />
                    <SkeletonBase
                      key={`role-management-${FIELDS_NAMES.DESCRIPTION}-field-loader`}
                      width={TEXTAREA_WIDTH}
                      height={TEXTAREA_HEIGHT}
                    />
                  </div>
                </>
                :
                <>
                  <Input
                    name={FIELDS_NAMES.TITLE}
                    label={t('innerLayout.userManagementPage.form.title')}
                    value={fields[FIELDS_NAMES.TITLE].value}
                    onChange={(e) => onChangeHandler(FIELDS_NAMES.TITLE, e.target.value)}
                    disabled={isInputsDisabled}
                    error={fields[FIELDS_NAMES.TITLE].errorMsg}
                    status={fields[FIELDS_NAMES.TITLE].errorMsg ? 'error' : 'default'}
                  />

                  <TextArea
                    name={FIELDS_NAMES.DESCRIPTION}
                    label={t('innerLayout.userManagementPage.form.description')}
                    value={fields[FIELDS_NAMES.DESCRIPTION].value}
                    onChange={(e) => onChangeHandler(FIELDS_NAMES.DESCRIPTION, e.target.value)}
                    disabled={isInputsDisabled}
                    errorMessage={fields[FIELDS_NAMES.DESCRIPTION].errorMsg}
                    variant={fields[FIELDS_NAMES.DESCRIPTION].errorMsg ? 'error' : 'default'}
                  />
                </>
            }
          </div>

        </div>

        <div className={styles["role-management__section"]}>

          <div className={styles["role-management__section__text-box"]}>

            <div className={styles["role-management__section__text-box__title"]}>
              {t('innerLayout.userManagementPage.roleManagementPage.permssions')}
              {
                pageType === 'create_new_role' ?
                  (permsCatalog && `(${permsCatalog?.length})`)
                  :
                  (retrievedRole?.permissions && `(${retrievedRole.permissions.length})`)
              }
            </div>
            <div className={styles["role-management__section__text-box__desc"]}>
              {t('innerLayout.userManagementPage.roleManagementPage.permssionsHint')}
            </div>
          </div>

          <div className={styles["role-management__section__actions-box"]}>
            {
              isPermsSectionLoading ?
                Array.from({ length: 6 }).map((_, index) =>
                  <SkeletonLoader
                    key={`role-management-collapsible-section-${index}-loader`}
                    width={COLLAPSIBLE_SECTION_WIDTH}
                    height={COLLAPSIBLE_SECTION_HEIGHT}
                    noOfRows={1}
                  />
                )
                :
                isPermsSectionError ?
                  <ComponentError
                    text={permsSectionError}
                  />
                  :
                  permsSectionListing?.map((section: any, index: number) =>
                    <CollapsibleSection
                      key={`${pageType}-role-set-${index}`}
                      title={section.title}
                      count={section.sub_list?.length || 0}
                      body={
                        <div className={styles["role-management__section__actions-box__collapsible-section-body"]}>
                          {
                            section.sub_list?.map((perm: any) =>
                              <Checkbox
                                key={`${pageType}-role-${perm.perm_name}`}
                                id={`${pageType}-role-${perm.perm_name}`}
                                text={perm.title}
                                checked={isChecked(perm)}
                                disabled={preventEdit}
                                onChangeHandler={(checked) =>
                                  onPermsChangeHandler(
                                    FIELDS_NAMES.PERMISSIONS,
                                    section.sub_list,
                                    perm.perm_name,
                                    perm.dependent,
                                    perm.hidden_perms,
                                    checked,
                                    'change'
                                  )
                                }
                                onClickHandler={(checked) =>
                                  onPermsChangeHandler(
                                    FIELDS_NAMES.PERMISSIONS,
                                    section.sub_list,
                                    perm.perm_name,
                                    perm.dependent,
                                    perm.hidden_perms,
                                    checked,
                                    'click'
                                  )
                                }
                              />
                            )
                          }
                        </div>
                      }
                    />
                  )
            }
          </div>

        </div>

        {
          !preventEdit &&
          <div className={styles["role-management__section"]}>

            <div className={styles["role-management__section__action-btns-box"]}>
              {
                actionBtnsProps.map((btnProps, index) =>
                  <Button
                    key={`${pageType}-action-btn-${index}`}
                    size="xl"
                    {...btnProps}
                  />
                )
              }
            </div>

          </div>
        }

      </div>

      <Notification
        {
        ...NotificationSharedProps({
          open: toasterInfo.open,
          state: toasterInfo.state,
          text: toasterInfo.text,
          hint: toasterInfo.hint,
          onCloseHandler: () => handleToasterClose(setToasterInfo),
          onShowHandler: () => onToasterShowHandler({
            type: toasterInfo.state,
            trackingCode: toasterInfo.trackingCode,
            msg: toasterInfo.text || toasterInfo.hint,
            srcFeature: SYSTEM_EVENTS_SRC_FEATURES.USERS_AND_PERMS
          })
        })
        }
      />

      <CancelRoleCreationModal
        modalProps={{
          state: "danger",
          open: isCancelRoleCreationModalOpen,
          title: t('innerLayout.userManagementPage.modals.cancelRoleCreationModal.title'),
          supportingText: t('innerLayout.userManagementPage.modals.cancelRoleCreationModal.supportingText'),
          btnsProps: [
            {
              size: "lg",
              color: "danger",
              variant: "contained",
              text: t('innerLayout.userManagementPage.modals.cancelRoleCreationModal.primaryBtnText'),
              onClickHandler: () => {
                navigate(INNER_ROUTES.MANAGE_ROLES);
              },
            },
            {
              size: "lg",
              color: "secondary",
              variant: "outlined",
              text: t('innerLayout.userManagementPage.modals.cancelRoleCreationModal.secondaryBtnText'),
              onClickHandler: () => setIsCancelRoleCreationModalOpen(false),
            }
          ]
        }}
      />

      {
        isRoleDeletable &&
        <DeleteRoleModal
          type='deleteRole'
          roleId={roleId}
          open={isDeleteRoleModalOpen}
          setOpen={setIsDeleteRoleModalOpen}
        />
      }
    </>
  )
}